/**
 * Calendario de temporadas.
 *
 * Una temporada es un rango de fechas (mes/día, sin año: se repite cada año) y un
 * conjunto de imágenes por "ranura" de la interfaz, organizado en VARIANTES: la
 * variante `default` es la que se ve al cargar; las demás se activan al hacer clic
 * sobre una imagen que lo pida (`onClick: { variant: '…' }`). Una imagen también
 * puede disparar un efecto visual (`onClick: { effect: '…' }`).
 *
 * Fuera de todo rango la app se ve exactamente como hoy: el componente de decoración
 * no pinta nada y no carga ninguna imagen.
 *
 * Los rangos NO deben solaparse; si lo hicieran gana el primero de la lista. Un rango
 * puede cruzar el fin de año (p. ej. 11-nov → 6-ene).
 *
 * Esta tabla vive en el código a propósito (opción 1 del análisis del 2026-09-22):
 * cambiar una fecha es un merge y un deploy. Si algún día operación necesita moverlas
 * sin ingeniería, la tabla se muda a la configuración del sistema (SYSTEM#SEASONAL)
 * y este archivo pasa a ser el valor por defecto.
 */

export type SeasonId = 'dia-de-muertos' | 'navidad';

export type Slot = 'header' | 'sidebar';

export type EffectId = 'slime' | 'countdown';

/** Bandas decorativas que acompañan a la cintilla de la cabecera mientras dure la temporada. */
export type BandId = 'lightrope';

export type ClickAction = { variant: string } | { effect: EffectId };

export interface MonthDay {
  month: number; // 1-12
  day: number; // 1-31
}

export interface SeasonAsset {
  /** Ruta relativa a /public */
  src: string;
  /**
   * Versión fija de la imagen para `prefers-reduced-motion: reduce` (E07-F01, R10):
   * un GIF no se puede detener por CSS, así que se pinta otra imagen en su lugar.
   * Obligatoria en todo `src` que termina en `.gif`; una guardia lo afirma.
   */
  still?: string;
  /** Texto alternativo; vacío = decorativa */
  alt: string;
  /** Alto en px al que se pinta (el ancho se ajusta solo) */
  height: number;
  /** Qué pasa al hacer clic. Sin esto la imagen no reacciona ni recibe el puntero. */
  onClick?: ClickAction;
}

export type VariantAssets = Record<Slot, SeasonAsset[]>;

export interface Season {
  id: SeasonId;
  label: string;
  /** Inclusivo */
  start: MonthDay;
  /** Inclusivo */
  end: MonthDay;
  /** `default` es obligatoria; las demás se alcanzan por clic. */
  variants: { default: VariantAssets } & Record<string, VariantAssets>;
  /** Banda bajo la cintilla de la cabecera (p. ej. la serie de luces). */
  headerBand?: BandId;
}

const DDM = '/seasonal/dia-de-muertos';
const HW = '/seasonal/halloween';
const NAV = '/seasonal/navidad';

const DDM_MIDDLE: SeasonAsset[] = [
  { src: `${DDM}/guirnalda.png`, alt: '', height: 36 },
  // Clic en este papel picado: la cabecera y la barra pasan a Halloween.
  { src: `${DDM}/papel-2.png`, alt: 'Papel picado', height: 36, onClick: { variant: 'halloween' } },
  { src: `${DDM}/papel.png`, alt: '', height: 36 },
];

export const SEASONS: Season[] = [
  {
    id: 'dia-de-muertos',
    label: 'Día de Muertos',
    start: { month: 10, day: 1 },
    end: { month: 11, day: 3 },
    variants: {
      default: {
        header: [
          { src: `${DDM}/catrina.gif`, still: `${DDM}/catrina.png`, alt: 'Catrina', height: 44, onClick: { variant: 'pan' } },
          ...DDM_MIDDLE,
          { src: `${DDM}/catrina-2.gif`, still: `${DDM}/catrina.png`, alt: 'Catrina', height: 44, onClick: { variant: 'pan' } },
        ],
        sidebar: [
          { src: `${DDM}/catrina.png`, alt: 'Catrina', height: 72 },
          { src: `${DDM}/ofrenda.png`, alt: 'Ofrenda de Día de Muertos', height: 112 },
        ],
      },
      // Clic en una catrina: las dos se vuelven pan de muerto; clic en un pan: vuelven.
      pan: {
        header: [
          { src: `${DDM}/pan.gif`, still: `${DDM}/pan-de-muerto.png`, alt: 'Pan de muerto', height: 44, onClick: { variant: 'default' } },
          ...DDM_MIDDLE,
          { src: `${DDM}/pan.gif`, still: `${DDM}/pan-de-muerto.png`, alt: 'Pan de muerto', height: 44, onClick: { variant: 'default' } },
        ],
        sidebar: [
          { src: `${DDM}/catrina.png`, alt: 'Catrina', height: 72 },
          { src: `${DDM}/ofrenda.png`, alt: 'Ofrenda de Día de Muertos', height: 112 },
        ],
      },
      // Halloween: clic en la momia vuelve al inicio; clic en la casa embrujada, baba.
      halloween: {
        header: [
          { src: `${HW}/horror-ghost.gif`, still: `${HW}/horror-ghost-still.png`, alt: 'Fantasma', height: 44 },
          { src: `${HW}/frankenstein.png`, alt: 'Frankenstein', height: 36 },
          { src: `${HW}/mummy.png`, alt: 'Momia', height: 36, onClick: { variant: 'default' } },
          { src: `${HW}/dracula.png`, alt: 'Drácula', height: 36 },
          { src: `${HW}/pumpkin.gif`, still: `${HW}/pumpkin-still.png`, alt: 'Calabaza', height: 44 },
        ],
        sidebar: [
          { src: `${HW}/haunted-house.png`, alt: 'Casa embrujada', height: 128, onClick: { effect: 'slime' } },
        ],
      },
    },
  },
  {
    id: 'navidad',
    label: 'Navidad',
    start: { month: 11, day: 4 },
    end: { month: 1, day: 6 },
    headerBand: 'lightrope',
    variants: {
      default: {
        header: [
          { src: `${NAV}/santa-claus.png`, alt: 'Santa Claus', height: 40 },
          { src: `${NAV}/melchor.gif`, still: `${NAV}/melchor-still.png`, alt: 'Melchor', height: 44 },
          { src: `${NAV}/gaspar.gif`, still: `${NAV}/gaspar-still.png`, alt: 'Gaspar', height: 44 },
          { src: `${NAV}/baltazar.gif`, still: `${NAV}/baltazar-still.png`, alt: 'Baltazar', height: 44 },
          { src: `${NAV}/snowman.png`, alt: 'Muñeco de nieve', height: 40 },
        ],
        sidebar: [
          // Clic en el regalo: cuenta regresiva a Navidad, al centro de la pantalla.
          { src: `${NAV}/christmas-present.png`, alt: 'Regalo', height: 112, onClick: { effect: 'countdown' } },
        ],
      },
    },
  },
];

const dayOfYear = (md: MonthDay): number => md.month * 100 + md.day;

/** ¿`date` cae dentro del rango [start, end] (inclusivo), con cruce de año? */
export const isWithin = (date: Date, start: MonthDay, end: MonthDay): boolean => {
  const d = dayOfYear({ month: date.getMonth() + 1, day: date.getDate() });
  const s = dayOfYear(start);
  const e = dayOfYear(end);
  return s <= e ? d >= s && d <= e : d >= s || d <= e;
};

/** La temporada activa en `date`, o null si la interfaz va sin decoración. */
export const resolveSeason = (date: Date, seasons: Season[] = SEASONS): Season | null =>
  seasons.find((s) => isWithin(date, s.start, s.end)) ?? null;
